import React, { useEffect, useState } from "react";
import { formatSlotSchedule } from "../utils/slotSchedule";
import { Input } from "../components/ui";
import { formatPartialNational } from "../utils/phoneFormat";
import PhoneInput from "../components/ui/PhoneInput";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useTheme } from "../context/ThemeContext";
import {
  getPageBackground,
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getInputStyles,
  getButtonStyles,
} from "../utils/themeUtils";
import {
  getTrialSettings,
  submitTrialSignup,
  TrialSettings,
  TrialSignupData,
  TrialAthlete,
  AdditionalContact,
} from "../api/trialAPI";
import { getPrograms } from "../api/programsAPI";
import { getProgramSlots } from "../api/programSlotAPI";
import { checkEmail } from "../api/membershipAPI";
import { US_STATES } from "../utils/helpers/listOfStates";
import { CheckCircle, Mail, AlertCircle, Loader2, Plus, Trash2 } from "lucide-react";

/** "#2 · Mon/Wed 4:00 PM": the class number (when there is one) and when it meets. */
export const trialSlotLabel = (slot: { slot_id?: string | number | null; times?: (string | null)[] | null }): string => {
  const raw = slot?.slot_id == null ? "" : String(slot.slot_id).trim();
  const number = raw === "" ? "" : raw.startsWith("#") ? raw : `#${raw}`;
  const schedule = formatSlotSchedule(slot?.times);
  return [number, schedule].filter(Boolean).join(" · ") || "Class time";
};

const TrialSignup: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  // Get tenant from URL
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const tenantSlug = pathParts[0] || "";

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [trialSettings, setTrialSettings] = useState<TrialSettings | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [successEmail, setSuccessEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  // Programs for class picker
  const [programs, setPrograms] = useState<any[]>([]);
  const [selectedProgramSlots, setSelectedProgramSlots] = useState<any[]>([]);
  // Every class time, read on its own: the programs list carries no class
  // times, so the Time Slot list was always empty (2026-10-11).
  const [allSlots, setAllSlots] = useState<any[]>([]);

  // Form data
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    alternate_email: "",
    mobile_sms: "",
    carrier: "",
    referral: "",
    alternate_phone: "",
    // Trial class
    trial_program_id: "",
    trial_slot_id: "",
    trial_date: "",
  });

  // Athletes
  const [athletes, setAthletes] = useState<TrialAthlete[]>([
    { first_name: "", last_name: "", dob: "", gender: "" },
  ]);

  // Additional contacts
  const [additionalContacts, setAdditionalContacts] = useState<AdditionalContact[]>([
    {
      first_name: "",
      last_name: "",
      home_phone: "",
      work_phone: "",
      mobile_phone: "",
      contact_type: "emergency",
    },
  ]);

  const [showAdditionalContacts, setShowAdditionalContacts] = useState(false);

  // Fetch trial settings and programs on mount
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      const [settingsResponse, programsResponse, slotsResponse] = await Promise.all([
        getTrialSettings(),
        getPrograms().catch(() => null),
        getProgramSlots().catch(() => null),
      ]);
      const slotList = slotsResponse?.data?.data;
      if (Array.isArray(slotList)) {
        setAllSlots(slotList);
      }
      if (settingsResponse.success && settingsResponse.data.trial_enabled) {
        setTrialSettings(settingsResponse.data);
      } else {
        setTrialSettings(null);
      }
      if (programsResponse?.data?.data) {
        setPrograms(programsResponse.data.data);
      }
      setLoading(false);
    };
    fetchSettings();
  }, []);

  // Update slots when program changes
  useEffect(() => {
    if (formData.trial_program_id) {
      setSelectedProgramSlots(allSlots.filter((slot: any) => slot.program_id === formData.trial_program_id));
    } else {
      setSelectedProgramSlots([]);
    }
  }, [formData.trial_program_id, allSlots]);

  // Phone formatting
  /**
   * Delegates to the shared formatter. This was a local copy — one of four
   * identical ones — and none of them capped at ten digits, so an over-long
   * paste kept its tail. See utils/phoneFormat.
   */
  const formatPhoneNumber = (value: string) => formatPartialNational(value);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const formatted = formatPhoneNumber(value);
    setFormData((prev) => ({ ...prev, [name]: formatted }));
  };

  const handleEmailBlur = async () => {
    if (!formData.email) {
      setEmailError(null);
      return;
    }
    try {
      const response = await checkEmail({ email: formData.email });
      if (response?.status === 200) {
        setEmailError(null);
      } else if (response?.status === 226) {
        setEmailError(response?.data?.message || "Email already exists");
      }
    } catch {
      setEmailError(null);
    }
  };

  const handleContactChange = (
    index: number,
    field: keyof AdditionalContact,
    value: string
  ) => {
    const updated = [...additionalContacts];
    updated[index] = { ...updated[index], [field]: value };
    setAdditionalContacts(updated);
  };

  const handleAthleteChange = (idx: number, field: keyof TrialAthlete, value: string) => {
    const updated = [...athletes];
    updated[idx] = { ...updated[idx], [field]: value };
    setAthletes(updated);
  };

  const addAthlete = () => {
    setAthletes([...athletes, { first_name: "", last_name: "", dob: "", gender: "" }]);
  };

  const removeAthlete = (idx: number) => {
    if (athletes.length > 1) {
      setAthletes(athletes.filter((_, i) => i !== idx));
    }
  };

  const addContact = () => {
    setAdditionalContacts([
      ...additionalContacts,
      {
        first_name: "",
        last_name: "",
        home_phone: "",
        work_phone: "",
        mobile_phone: "",
        contact_type: "emergency",
      },
    ]);
  };

  const removeContact = (index: number) => {
    if (additionalContacts.length > 1) {
      setAdditionalContacts(additionalContacts.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (emailError) {
      toast.error("Please use a different email address");
      return;
    }

    setSubmitting(true);

    const mappedAthletes: TrialAthlete[] = athletes.map((a, i) => ({
      first_name: a.first_name || (i === 0 ? formData.firstName : ""),
      last_name: a.last_name || (i === 0 ? formData.lastName : ""),
      dob: a.dob || undefined,
      gender: a.gender || undefined,
    }));

    const signupData: TrialSignupData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone: formData.phone || undefined,
      address: formData.address || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      zip: formData.zip || undefined,
      tenant_id: tenantSlug,
      alternate_email: formData.alternate_email || undefined,
      mobile_sms: formData.mobile_sms || undefined,
      carrier: formData.carrier || undefined,
      referral: formData.referral || undefined,
      alternate_phone: formData.alternate_phone || undefined,
      additionalContacts: showAdditionalContacts
        ? additionalContacts.filter((c) => c.first_name || c.last_name)
        : undefined,
      athletes: mappedAthletes,
      // Trial class
      trial_program_id: formData.trial_program_id || undefined,
      trial_slot_id: formData.trial_slot_id || undefined,
      trial_date: formData.trial_date || undefined,
    };

    const response = await submitTrialSignup(signupData);

    setSubmitting(false);

    if (response.success) {
      setSuccessEmail(formData.email);
      setSignupSuccess(true);
      toast.success("Trial signup successful!");
    } else {
      toast.error(response.message || "Failed to sign up for trial");
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div
          className={`flex-1 ${getPageBackground(isDarkMode)} flex items-center justify-center`}
        >
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
        <Footer />
      </div>
    );
  }

  // Trial not enabled
  if (!trialSettings || !trialSettings.trial_enabled) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div
          className={`flex-1 ${getPageBackground(isDarkMode)} flex items-center justify-center`}
        >
          <div
            className={`${getCardBackground(isDarkMode)} rounded-lg shadow-lg p-8 max-w-md text-center`}
          >
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className={`text-2xl font-bold ${getPrimaryText(isDarkMode)} mb-2`}>
              Trial Signup Not Available
            </h2>
            <p className={`${getSecondaryText(isDarkMode)} mb-6`}>
              Trial signups are not currently available for this organization.
            </p>
            <button
              onClick={() => navigate(`/${tenantSlug}`)}
              className={`${getButtonStyles(isDarkMode)} px-6 py-2 rounded-md`}
            >
              Return to Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Success state
  if (signupSuccess) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div
          className={`flex-1 ${getPageBackground(isDarkMode)} flex items-center justify-center p-4`}
        >
          <div
            className={`${getCardBackground(isDarkMode)} rounded-lg shadow-lg p-8 max-w-lg text-center`}
          >
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
            <h2 className={`text-2xl font-bold ${getPrimaryText(isDarkMode)} mb-2`}>
              Trial Signup Received!
            </h2>
            <p className={`${getSecondaryText(isDarkMode)} mb-6`}>
              Thank you for your interest! We&apos;ll be in touch to confirm your trial class.
            </p>
            <div
              className={`${isDarkMode ? "bg-blue-900/30" : "bg-blue-50"} rounded-lg p-4 mb-6`}
            >
              <Mail className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              {/* No email is sent at signup: staff confirm the trial class
                  themselves. This used to say "Confirmation sent to:". */}
              <p className={`${getPrimaryText(isDarkMode)} font-medium`}>
                We&apos;ll contact you at:
              </p>
              <p className={`${getPrimaryText(isDarkMode)} font-medium mt-1`}>
                {successEmail}
              </p>
            </div>
            <p className={`${getSecondaryText(isDarkMode)} text-sm mb-6`}>
              Our team will reach out to schedule your trial class. We look forward to seeing you!
            </p>
            <button
              onClick={() => navigate(`/${tenantSlug}`)}
              className={`${getButtonStyles(isDarkMode)} px-6 py-2 rounded-md`}
            >
              Return Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Main form
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <div className={`flex-1 ${getPageBackground(isDarkMode)} py-8 px-4`}>
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            {trialSettings.logo_url && (
              <img
                src={trialSettings.logo_url}
                alt={trialSettings.business_name}
                className="h-16 mx-auto mb-4"
              />
            )}
            <h1 className={`text-3xl font-bold ${getPrimaryText(isDarkMode)} mb-2`}>
              Start Your Free {trialSettings.trial_days}-Day Trial
            </h1>
            <p className={`${getSecondaryText(isDarkMode)}`}>
              {trialSettings.trial_terms_text}
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className={`${getCardBackground(isDarkMode)} rounded-lg shadow-lg p-6`}
          >
            {/* Contact Information */}
            <h3 className={`text-xl font-semibold ${getPrimaryText(isDarkMode)} mb-4`}>
              Contact Information
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <Input
                  label="First Name *"
                  type="text"
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Input
                  label="Last Name *"
                  type="text"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Input
                  label="Email *"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleEmailBlur}
                  required
                  className={`${emailError ? "border-red-500" : ""}`}
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-1">{emailError}</p>
                )}
              </div>
              <div>
                <label
                  className={`block text-sm font-medium ${getSecondaryText(isDarkMode)} mb-1`}
                >
                  Phone
                </label>
                <PhoneInput
                  name="phone"
                  value={formData.phone}
                  onChange={(d) =>
                    handlePhoneInput({
                      target: { name: "phone", value: formatPartialNational(d) },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  className={`w-full px-3 py-2 rounded-md ${getInputStyles(isDarkMode)}`}
                />
              </div>
            </div>

            {/* Address */}
            <h3 className={`text-xl font-semibold ${getPrimaryText(isDarkMode)} mb-4`}>
              Address
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="sm:col-span-2">
                <Input
                  label="Street Address"
                  type="text"
                  name="address"
                  autoComplete="address-line1"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Input
                  label="City"
                  type="text"
                  name="city"
                  autoComplete="address-level2"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label
                  className={`block text-sm font-medium ${getSecondaryText(isDarkMode)} mb-1`}
                >
                  State
                </label>
                <select
                  name="state"
                  autoComplete="address-level1"
                  value={formData.state}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 rounded-md ${getInputStyles(isDarkMode)}`}
                >
                  <option value="">Select State</option>
                  {US_STATES.map((state) => (
                    <option key={state.abbreviation} value={state.abbreviation}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Input
                  label="ZIP Code"
                  type="text"
                  name="zip"
                  autoComplete="postal-code"
                  inputMode="numeric"
                  value={formData.zip}
                  onChange={handleChange}
                  maxLength={10}
                />
              </div>
              <div>
                <Input
                  label="How did you hear about us?"
                  type="text"
                  name="referral"
                  value={formData.referral}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Athlete(s) */}
            <h3 className={`text-xl font-semibold ${getPrimaryText(isDarkMode)} mb-4`}>
              Athlete(s)
            </h3>
            <div className="space-y-4 mb-6">
              {athletes.map((athlete, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-md ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className={`font-medium ${getSecondaryText(isDarkMode)}`}>
                      Athlete {idx + 1}
                    </span>
                    {athletes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAthlete(idx)}
                        className="text-red-500 text-sm flex items-center gap-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Input
                        label="First Name"
                        type="text"
                        value={athlete.first_name}
                        onChange={(e) => handleAthleteChange(idx, "first_name", e.target.value)}
                        placeholder={idx === 0 ? formData.firstName || "First Name" : "First Name"}
                      />
                    </div>
                    <div>
                      <Input
                        label="Last Name"
                        type="text"
                        value={athlete.last_name}
                        onChange={(e) => handleAthleteChange(idx, "last_name", e.target.value)}
                        placeholder={idx === 0 ? formData.lastName || "Last Name" : "Last Name"}
                      />
                    </div>
                    <div>
                      <Input
                        label="Date of Birth"
                        type="date" autoComplete="off"
                        value={athlete.dob || ""}
                        onChange={(e) => handleAthleteChange(idx, "dob", e.target.value)}
                      />
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium ${getSecondaryText(isDarkMode)} mb-1`}
                      >
                        Gender
                      </label>
                      <select
                        value={athlete.gender || ""}
                        onChange={(e) => handleAthleteChange(idx, "gender", e.target.value)}
                        className={`w-full px-3 py-2 rounded-md ${getInputStyles(isDarkMode)}`}
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                        <option value="prefer_not_to_say">Prefer not to say</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addAthlete}
                className="flex items-center gap-1.5 text-blue-500 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Another Athlete
              </button>
            </div>

            {/* Trial Class Selection */}
            {programs.length > 0 && (
              <>
                <h3 className={`text-xl font-semibold ${getPrimaryText(isDarkMode)} mb-4`}>
                  Select a Class (Optional)
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label
                      htmlFor="trial_program_id"
                      className={`block text-sm font-medium ${getSecondaryText(isDarkMode)} mb-1`}
                    >
                      Program
                    </label>
                    <select
                      id="trial_program_id"
                      name="trial_program_id"
                      value={formData.trial_program_id}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 rounded-md ${getInputStyles(isDarkMode)}`}
                    >
                      <option value="">Select a program</option>
                      {programs.map((program: any) => (
                        <option key={program.id} value={program.id}>
                          {program.label || program.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium ${getSecondaryText(isDarkMode)} mb-1`}
                    >
                      Time Slot
                    </label>
                    <select
                      name="trial_slot_id"
                      value={formData.trial_slot_id}
                      onChange={handleChange}
                      disabled={!formData.trial_program_id}
                      className={`w-full px-3 py-2 rounded-md ${getInputStyles(isDarkMode)} ${!formData.trial_program_id ? "opacity-50" : ""}`}
                    >
                      <option value="">Select a time slot</option>
                      {selectedProgramSlots.map((slot: any) => (
                        // The class's days and time, after its number: the
                        // number alone ("#2") told a family nothing.
                        <option key={slot.id} value={slot.id}>
                          {trialSlotLabel(slot)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Input
                      label="Preferred Trial Date"
                      type="date" autoComplete="off"
                      name="trial_date"
                      value={formData.trial_date}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Emergency Contacts (Collapsible) */}
            <div className="mb-6">
              <button
                type="button"
                onClick={() => setShowAdditionalContacts(!showAdditionalContacts)}
                className={`flex items-center gap-2 ${getPrimaryText(isDarkMode)} font-medium`}
              >
                <span>{showAdditionalContacts ? "−" : "+"}</span>
                Emergency Contacts (Optional)
              </button>

              {showAdditionalContacts && (
                <div className="mt-4 space-y-4">
                  {additionalContacts.map((contact, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-md ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
                    >
                      <div className="flex justify-between items-center mb-3">
                        <span
                          className={`font-medium ${getSecondaryText(isDarkMode)}`}
                        >
                          Emergency Contact {index + 1}
                        </span>
                        {additionalContacts.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeContact(index)}
                            className="text-red-500 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                          aria-label={`Contact ${index + 1} first name`}
                          type="text"
                          placeholder="First Name"
                          value={contact.first_name || ""}
                          onChange={(e) =>
                            handleContactChange(index, "first_name", e.target.value)
                          }
                          className={`px-3 py-2 rounded-md ${getInputStyles(isDarkMode)}`}
                        />
                        <input
                          aria-label={`Contact ${index + 1} last name`}
                          type="text"
                          placeholder="Last Name"
                          value={contact.last_name || ""}
                          onChange={(e) =>
                            handleContactChange(index, "last_name", e.target.value)
                          }
                          className={`px-3 py-2 rounded-md ${getInputStyles(isDarkMode)}`}
                        />
                        <PhoneInput
                          placeholder="Mobile Phone"
                          value={contact.mobile_phone || ""}
                          onChange={(d) =>
                            handleContactChange(
                              index,
                              "mobile_phone",
                              formatPartialNational(d)
                            )
                          }
                          className={`px-3 py-2 rounded-md ${getInputStyles(isDarkMode)}`}
                        />
                        <select
                          value={contact.contact_type || "emergency"}
                          onChange={(e) =>
                            handleContactChange(index, "contact_type", e.target.value)
                          }
                          className={`px-3 py-2 rounded-md ${getInputStyles(isDarkMode)}`}
                        >
                          <option value="emergency">Emergency Contact</option>
                          <option value="authorized_pickup">Authorized Pickup</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addContact}
                    className="text-blue-500 text-sm font-medium"
                  >
                    + Add Another Contact
                  </button>
                </div>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting || !!emailError}
              className={`w-full py-3 rounded-md font-semibold ${
                submitting || emailError
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)]"
              } transition-colors`}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating your account...
                </span>
              ) : (
                `Start My Free ${trialSettings.trial_days}-Day Trial`
              )}
            </button>

            <p className={`text-center text-sm ${getSecondaryText(isDarkMode)} mt-4`}>
              Already have an account?{" "}
              <a
                href={`/${tenantSlug}/login`}
                className="text-blue-500 hover:underline"
              >
                Log in
              </a>
            </p>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TrialSignup;
